import { commandPayload, commands } from '../../lib/runtime'
import { isNativeShell } from '../../lib/shell'

const OWNER_KEY = 'chow:push-owner'
let activeRegistration: ServiceWorkerRegistration | undefined
let activeSubscription: PushSubscription | null = null
export const pushSupported = () => !isNativeShell() && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
const storedOwner = () => { try { return localStorage.getItem(OWNER_KEY) || '' } catch { return '' } }

const workerOwner = (profileId: string) => activeRegistration?.active?.postMessage({ type: 'CHOW_PUSH_OWNER', profileId })

export async function pushState(profileId: string, renew = false): Promise<boolean> {
  if (!pushSupported()) return false
  activeRegistration = await navigator.serviceWorker.getRegistration('/')
  activeSubscription = await activeRegistration?.pushManager.getSubscription() ?? null
  if (storedOwner() !== profileId) { workerOwner(''); return false }
  if (activeSubscription) {
    workerOwner(profileId)
    if (renew && Notification.permission === 'granted') {
      const json = activeSubscription.toJSON()
      await commands.notifications.subscribe({ profile_id: profileId, endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth })
    }
  }
  return Boolean(activeSubscription && Notification.permission === 'granted')
}

async function readyWorker(): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([navigator.serviceWorker.ready, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Notification setup timed out. Try again.')), 10000)
    })])
  } finally { clearTimeout(timer) }
}

export async function enablePush(profileId: string, publicKey: string): Promise<void> {
  if (!pushSupported()) throw new Error('Use a supported browser. On iPhone, add ChooseChow to your Home Screen first.')
  if (!profileId || !publicKey) throw new Error('Notifications are not configured yet.')
  if (await Notification.requestPermission() !== 'granted') throw new Error('Notifications are blocked. You can change this in your browser settings.')
  const key = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))
  await navigator.serviceWorker.register('/sw.js')
  activeRegistration = await readyWorker()
  activeSubscription = await activeRegistration.pushManager.getSubscription()
  if (activeSubscription && storedOwner() !== profileId) { await activeSubscription.unsubscribe(); activeSubscription = null }
  activeSubscription ??= await activeRegistration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  const json = activeSubscription.toJSON()
  try {
    await commands.notifications.subscribe({ profile_id: profileId, endpoint: json.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth })
    localStorage.setItem(OWNER_KEY, profileId)
    workerOwner(profileId)
  } catch (error) {
    await activeSubscription.unsubscribe().catch(() => false)
    activeSubscription = null
    workerOwner('')
    throw error
  }
}

export async function disablePush(): Promise<void> {
  workerOwner('')
  try { localStorage.removeItem(OWNER_KEY) } catch { /* The worker still clears its owner. */ }
  const sub = activeSubscription
  activeSubscription = null
  if (!sub) return
  const results = await Promise.allSettled([commands.notifications.unsubscribe(sub.endpoint), sub.unsubscribe()])
  const browser = results[1]
  if (browser.status === 'rejected' || !browser.value) throw new Error('The browser could not remove notifications. Check its site settings.')
  // Browser revocation prevents delivery even when the server is temporarily unavailable.
}

/** Start revocation before the authentication token is removed. */
export function clearPushSession(): void {
  workerOwner('')
  if (activeSubscription) void disablePush().catch(() => {})
  else if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.getRegistration('/').then((registration) => {
      registration?.active?.postMessage({ type: 'CHOW_PUSH_OWNER', profileId: '' })
      return registration?.pushManager.getSubscription()
    }).then((sub) => sub?.unsubscribe()).catch(() => {})
    try { localStorage.removeItem(OWNER_KEY) } catch { /* Permission settings remain available. */ }
  }
}

export async function testPush(): Promise<void> {
  if (!activeSubscription) throw new Error('Enable notifications on this device first.')
  const payload = commandPayload(await commands.notifications.test(activeSubscription.endpoint))
  if (!payload.queued) throw new Error('The test notification could not be queued.')
}
